import { useEffect, useState } from "react";
import { Link } from "react-router";
import { useSelector } from "react-redux";
import { CalendarClock } from "lucide-react";
import PageShell from "../../components/dashboard/PageShell";
import StatCard from "../../components/dashboard/StatCard";
import StatCardGrid from "../../components/dashboard/StatCardGrid";
import StatusBadge from "../../components/dashboard/StatusBadge";
import { USE_MOCK_AUTH } from "../../config/api";
import { fetchOpsFollowUps } from "../../services/dashboardApi";
import { formatCurrency, formatDate } from "../../data/mock/dashboardData";
import { showError } from "../../utils/toast";
import { useLiveEventTick } from "../../hooks/useLiveRefresh";

function TaskList({ title, rows, empty, teamView }) {
  return (
    <section className="rounded-xl border border-primary/[0.07] bg-white p-5 shadow-[0_1px_2px_rgba(127,29,29,0.04)]">
      <h2 className="text-sm font-bold text-primary">{title}</h2>
      <ul className="mt-3 space-y-2">
        {rows.length === 0 ? <li className="text-xs text-primary/40">{empty}</li> : null}
        {rows.map((row) => (
          <li
            key={row.id}
            className="flex items-center justify-between gap-3 rounded-lg border border-primary/[0.05] px-3 py-2.5"
          >
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-primary">{row.customer}</p>
              <p className="text-[11px] text-primary/45">
                {row.caseId} · due {formatDate(row.dueDate)}
                {row.amount ? ` · ${formatCurrency(row.amount)} outstanding` : ""}
              </p>
              {teamView ? (
                <p className="mt-0.5 text-[11px] text-primary/55">
                  Call centre agent: {row.agent_name || "Unassigned"}
                  {" · "}
                  Call centre manager: {row.manager_name || "None assigned"}
                </p>
              ) : null}
              {row.notes ? <p className="mt-0.5 truncate text-[11px] text-primary/55">{row.notes}</p> : null}
            </div>
            <div className="flex shrink-0 items-center gap-3">
              <StatusBadge status={row.status === "pending" ? "pending" : "active"} label={row.status === "pending" ? "Pending" : "Upcoming"} />
              {teamView ? null : (
                <Link
                  to={`/dashboard/log-contact?caseId=${encodeURIComponent(row.caseId || "")}`}
                  className="text-[11px] font-semibold text-accent-dark"
                >
                  Log engagement
                </Link>
              )}
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

export default function FollowUpsPage() {
  const token = useSelector((state) => state.auth.token);
  const useLiveApi = Boolean(token) && !USE_MOCK_AUTH;
  const [board, setBoard] = useState({ pending: 0, upcoming: 0, total: 0, tasks: [] });
  const [loading, setLoading] = useState(useLiveApi);
  const liveTick = useLiveEventTick(/^(case\.|engagement\.)/);

  useEffect(() => {
    if (!useLiveApi) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    fetchOpsFollowUps(token)
      .then((data) => {
        if (!cancelled) setBoard(data);
      })
      .catch((error) => {
        if (!cancelled) showError(error.message || "Could not load follow-ups.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token, useLiveApi, liveTick]);

  const tasks = Array.isArray(board.tasks) ? board.tasks : [];
  const pending = tasks.filter((row) => row.status === "pending");
  const upcoming = tasks.filter((row) => row.status !== "pending");
  const teamView = board.team_follow_ups === true;

  return (
    <PageShell
      title="Follow-ups"
      description={
        teamView
          ? "Follow-ups for call centre managers and agents, including who is responsible. Pending means due today or overdue."
          : "Tasks scheduled from engagements. Pending means due today or overdue, with no later engagement logged."
      }
    >
      <StatCardGrid cols={3}>
        <StatCard
          label="Pending"
          value={loading ? "…" : String(board.pending)}
          sub="Due today or overdue"
          icon={CalendarClock}
          tone="primary"
        />
        <StatCard
          label="Upcoming"
          value={loading ? "…" : String(board.upcoming)}
          sub="Scheduled after today"
          icon={CalendarClock}
          tone="accent"
        />
        <StatCard
          label="Open tasks"
          value={loading ? "…" : String(board.total)}
          sub={teamView ? "Waiting on a manager or agent" : "Still waiting on a follow-up"}
          icon={CalendarClock}
          tone="primary"
        />
      </StatCardGrid>

      <div className="mt-4 space-y-4">
        <TaskList
          title={teamView ? "Pending for the team" : "Pending"}
          rows={pending}
          teamView={teamView}
          empty={loading ? "Loading follow-ups…" : "No pending follow-ups."}
        />
        <TaskList
          title="Upcoming"
          rows={upcoming}
          teamView={teamView}
          empty={loading ? "Loading follow-ups…" : "No upcoming follow-ups."}
        />
      </div>
    </PageShell>
  );
}
